<template>
    <div class="ngo-page" @click="closeAllCombos">
        <div class="ngo-header">
            <div>
                <h1>New Global Offers</h1>
                <p class="ngo-sub">Offers created via the new Affise flow</p>
            </div>
            <button type="button" class="ngo-primary-btn" @click="goCreate">Create global offer</button>
        </div>

        <div class="ngo-toolbar">
            <input
                class="ngo-input ngo-search"
                type="text"
                placeholder="Type to search offers…"
                v-model="searchQ"
            />

            <div
                v-for="field in comboFields"
                :key="field.key"
                class="ngo-combo"
                :class="{ 'is-open': comboOpen[field.key] }"
                @click.stop
            >
                <div class="ngo-combo-wrap">
                    <input
                        class="ngo-input ngo-combo-input"
                        type="text"
                        :placeholder="field.allLabel"
                        :value="comboQuery[field.key]"
                        @focus="openCombo(field.key)"
                        @input="onComboInput(field.key, $event.target.value)"
                    />
                    <button
                        v-if="filters[field.key] || comboQuery[field.key]"
                        type="button"
                        class="ngo-combo-clear"
                        aria-label="Clear"
                        @click="clearCombo(field.key)"
                    >
                        <i class="fa fa-times"></i>
                    </button>
                    <button
                        type="button"
                        class="ngo-combo-chevron"
                        aria-label="Toggle options"
                        @click="toggleCombo(field.key)"
                    >
                        <i class="fa" :class="comboOpen[field.key] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </button>
                </div>
                <ul v-if="comboOpen[field.key]" class="ngo-combo-dropdown">
                    <li>
                        <button type="button" :class="{ active: !filters[field.key] }" @click="selectCombo(field.key, '')">
                            {{ field.allLabel }}
                        </button>
                    </li>
                    <li v-for="opt in filteredComboOptions(field.key)" :key="field.key + '-' + opt">
                        <button
                            type="button"
                            :class="{ active: filters[field.key] === opt }"
                            @click="selectCombo(field.key, opt)"
                        >
                            {{ opt }}
                        </button>
                    </li>
                    <li v-if="!filteredComboOptions(field.key).length" class="ngo-combo-empty">No matches</li>
                </ul>
            </div>

            <button v-if="hasActiveFilters" type="button" class="ngo-clear-btn" @click="clearFilters">Clear</button>
        </div>

        <div class="ngo-card">
            <div v-if="loading && !offers.length" class="ngo-status">Loading offers…</div>
            <div v-else-if="error && !offers.length" class="ngo-error">{{ error }}</div>
            <div v-else-if="!offers.length && !hasActiveFilters" class="ngo-status">No offers yet. Create one to get started.</div>
            <div v-else-if="!offers.length" class="ngo-status">No offers match the current filters.</div>
            <template v-else>
                <div class="ngo-table-meta">
                    <div class="ngo-meta-left">
                        <p class="ngo-count">
                            Showing {{ rangeStart }}–{{ rangeEnd }} of {{ total }}
                            <span v-if="loading" class="ngo-muted"> · refreshing…</span>
                        </p>
                        <button
                            v-if="selectedCount > 0"
                            type="button"
                            class="ngo-primary-btn ngo-multi-edit-btn"
                            @click="goMultiEdit"
                        >
                            Edit multiple ({{ selectedCount }})
                        </button>
                        <button
                            v-if="selectedCount > 0"
                            type="button"
                            class="ngo-clear-btn"
                            @click="clearSelection"
                        >
                            Clear selection
                        </button>
                    </div>
                    <div class="ngo-limit">
                        <label class="ngo-muted">Per page</label>
                        <select class="ngo-input ngo-limit-select" :value="limit" @change="setLimit(Number($event.target.value))">
                            <option v-for="n in limitOptions" :key="n" :value="n">{{ n }}</option>
                        </select>
                    </div>
                </div>
                <div class="ngo-table-wrap">
                    <table class="ngo-table">
                        <thead>
                            <tr>
                                <th class="ngo-th-check">
                                    <input
                                        type="checkbox"
                                        :checked="allPageSelected"
                                        :indeterminate.prop="somePageSelected && !allPageSelected"
                                        @change="toggleSelectPage($event.target.checked)"
                                        aria-label="Select all on page"
                                    />
                                </th>
                                <th
                                    v-for="col in columns"
                                    :key="col.key"
                                    :class="{ 'ngo-th-sort': true, 'is-active': sort === col.key }"
                                    @click="setSort(col.key)"
                                >
                                    {{ col.label }}
                                    <i v-if="sort === col.key" :class="dir === 'asc' ? 'fa fa-caret-up' : 'fa fa-caret-down'"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="offer in offers"
                                :key="offer.id"
                                :class="{ 'is-selected': isSelected(offer.id) }"
                            >
                                <td class="ngo-td-check">
                                    <input
                                        type="checkbox"
                                        :checked="isSelected(offer.id)"
                                        @change="toggleSelect(offer, $event.target.checked)"
                                        :aria-label="'Select offer ' + offer.id"
                                    />
                                </td>
                                <td class="ngo-mono">{{ offer.id }}</td>
                                <td class="ngo-title">
                                    <button type="button" class="ngo-title-link" @click="goEdit(offer)">
                                        <span class="ngo-title-text">{{ offer.title }}</span>
                                        <i class="fa fa-pencil ngo-title-icon" aria-hidden="true"></i>
                                    </button>
                                </td>
                                <td class="ngo-mono ngo-affise-id">{{ offer.affise_offer_id != null ? offer.affise_offer_id : '—' }}</td>
                                <td>{{ shortAccount(offer.account) }}</td>
                                <td class="ngo-advertiser">
                                    <div v-if="offer.advertiser_name" class="ngo-advertiser-name">{{ offer.advertiser_name }}</div>
                                    <div class="ngo-mono">{{ offer.advertiser_id || '—' }}</div>
                                </td>
                                <td class="ngo-bundle">
                                    <div v-if="offer.bundle_name" class="ngo-bundle-name">{{ offer.bundle_name }}</div>
                                    <div class="ngo-mono">{{ offer.bundle_id || '—' }}</div>
                                </td>
                                <td>{{ offer.country || '—' }}</td>
                                <td>{{ offer.attribution || '—' }}</td>
                                <td>{{ offer.offer_type || '—' }}</td>
                                <td>{{ offer.mmp || '—' }}</td>
                                <td>{{ offer.updated_by || '—' }}</td>
                                <td class="ngo-muted">{{ formatDate(offer.updated_at) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="totalPages > 1" class="ngo-pager">
                    <button type="button" class="ngo-page-btn" :disabled="page <= 1 || loading" @click="goToPage(page - 1)">
                        Prev
                    </button>
                    <button
                        v-for="p in visiblePages"
                        :key="'p-' + p"
                        type="button"
                        class="ngo-page-btn"
                        :class="{ active: p === page, 'is-ellipsis': p === '…' }"
                        :disabled="p === '…' || loading"
                        @click="p !== '…' && goToPage(p)"
                    >
                        {{ p }}
                    </button>
                    <button type="button" class="ngo-page-btn" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)">
                        Next
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
const FILTER_KEYS = ['q', 'attribution', 'offer_type', 'mmp', 'country']
const COMBO_KEYS = ['attribution', 'offer_type', 'mmp', 'country']
const DEFAULT_SORT = 'id'
const DEFAULT_DIR = 'desc'
const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 50
const LIMIT_OPTIONS = [25, 50, 100, 200]
const SEARCH_DEBOUNCE_MS = 350

const COMBO_FIELDS = [
    { key: 'attribution', allLabel: 'All attribution' },
    { key: 'offer_type', allLabel: 'All types' },
    { key: 'mmp', allLabel: 'All MMP' },
    { key: 'country', allLabel: 'All countries' },
]

const SORT_OPTIONS = [
    { value: 'id', label: 'ID' },
    { value: 'title', label: 'Title' },
    { value: 'account', label: 'Account' },
    { value: 'advertiser_id', label: 'Advertiser' },
    { value: 'bundle_name', label: 'Bundle' },
    { value: 'country', label: 'Country' },
    { value: 'attribution', label: 'Attribution' },
    { value: 'offer_type', label: 'Type' },
    { value: 'mmp', label: 'MMP' },
    { value: 'affise_offer_id', label: 'Affise' },
    { value: 'updated_by', label: 'Updated by' },
    { value: 'updated_at', label: 'Updated at' },
]

function emptyFilters() {
    return {
        q: '',
        attribution: '',
        offer_type: '',
        mmp: '',
        country: '',
    }
}

function emptyFilterOptions() {
    return {
        attribution: [],
        offer_type: [],
        mmp: [],
        country: [],
    }
}

function emptyComboState() {
    const open = {}
    const query = {}
    COMBO_KEYS.forEach((key) => {
        open[key] = false
        query[key] = ''
    })
    return { open, query }
}

function filtersFromQuery(query) {
    const filters = emptyFilters()
    FILTER_KEYS.forEach((key) => {
        const value = query[key]
        if (value != null && String(value).trim() !== '') {
            filters[key] = String(value)
        }
    })
    return filters
}

function sortFromQuery(query) {
    const sort = String((query && query.sort) || DEFAULT_SORT)
    return SORT_OPTIONS.some((o) => o.value === sort) ? sort : DEFAULT_SORT
}

function dirFromQuery(query) {
    const dir = String((query && query.dir) || DEFAULT_DIR).toLowerCase()
    return dir === 'asc' ? 'asc' : 'desc'
}

function pageFromQuery(query) {
    const n = parseInt(String((query && query.page) || DEFAULT_PAGE), 10)
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_PAGE
}

function limitFromQuery(query) {
    const n = parseInt(String((query && query.limit) || DEFAULT_LIMIT), 10)
    if (!Number.isFinite(n) || n <= 0) return DEFAULT_LIMIT
    return LIMIT_OPTIONS.indexOf(n) !== -1 ? n : DEFAULT_LIMIT
}

function buildVisiblePages(page, totalPages) {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages = new Set([1, totalPages, page, page - 1, page + 1])
    if (page <= 3) {
        pages.add(2)
        pages.add(3)
        pages.add(4)
    }
    if (page >= totalPages - 2) {
        pages.add(totalPages - 1)
        pages.add(totalPages - 2)
        pages.add(totalPages - 3)
    }
    const sorted = Array.from(pages).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b)
    const out = []
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('…')
        out.push(sorted[i])
    }
    return out
}

function queryEqual(a, b) {
    const left = a || {}
    const right = b || {}
    const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)]))
    return keys.every((key) => String(left[key] || '') === String(right[key] || ''))
}

export default {
    name: 'NewGlobalOffers',
    data() {
        const combo = emptyComboState()
        return {
            loading: false,
            error: '',
            offers: [],
            total: 0,
            page: DEFAULT_PAGE,
            limit: DEFAULT_LIMIT,
            limitOptions: LIMIT_OPTIONS,
            filterOptions: emptyFilterOptions(),
            filters: emptyFilters(),
            searchQ: '',
            sort: DEFAULT_SORT,
            dir: DEFAULT_DIR,
            comboFields: COMBO_FIELDS,
            comboOpen: combo.open,
            comboQuery: combo.query,
            selectedIds: {},
            columns: [
                { key: 'id', label: 'ID' },
                { key: 'title', label: 'Title' },
                { key: 'affise_offer_id', label: 'Affise' },
                { key: 'account', label: 'Account' },
                { key: 'advertiser_id', label: 'Advertiser' },
                { key: 'bundle_name', label: 'Bundle' },
                { key: 'country', label: 'Country' },
                { key: 'attribution', label: 'Attr' },
                { key: 'offer_type', label: 'Type' },
                { key: 'mmp', label: 'MMP' },
                { key: 'updated_by', label: 'Updated by' },
                { key: 'updated_at', label: 'Updated at' },
            ],
        }
    },
    computed: {
        hasActiveFilters() {
            return FILTER_KEYS.some((key) => String(this.filters[key] || '').trim() !== '') ||
                String(this.searchQ || '').trim() !== '' ||
                this.sort !== DEFAULT_SORT ||
                this.dir !== DEFAULT_DIR ||
                this.page !== DEFAULT_PAGE ||
                this.limit !== DEFAULT_LIMIT
        },
        totalPages() {
            if (!this.total || !this.limit) return 1
            return Math.max(1, Math.ceil(this.total / this.limit))
        },
        rangeStart() {
            if (!this.total) return 0
            return (this.page - 1) * this.limit + 1
        },
        rangeEnd() {
            if (!this.total) return 0
            return Math.min(this.total, (this.page - 1) * this.limit + this.offers.length)
        },
        visiblePages() {
            return buildVisiblePages(this.page, this.totalPages)
        },
        selectedCount() {
            return Object.keys(this.selectedIds || {}).length
        },
        allPageSelected() {
            if (!this.offers.length) return false
            return this.offers.every((o) => this.isSelected(o.id))
        },
        somePageSelected() {
            return this.offers.some((o) => this.isSelected(o.id))
        },
    },
    watch: {
        searchQ() {
            if (this._ignoreSearchWatch) return
            this.scheduleSearchFetch()
        },
        '$route.query': {
            deep: true,
            handler(next, prev) {
                if (this._syncingFromRoute) return
                if (queryEqual(next, prev)) return
                this.applyQueryFromRoute()
                this.fetchOffers()
            },
        },
    },
    created() {
        this._searchTimer = null
        this._syncingFromRoute = false
        this._ignoreSearchWatch = false
        this.applyQueryFromRoute()
        this.fetchOffers()
    },
    beforeDestroy() {
        if (this._searchTimer) clearTimeout(this._searchTimer)
    },
    methods: {
        goCreate() {
            this.$router.push({ name: 'CreateGlobalOffer' })
        },
        goEdit(offer) {
            if (!offer || !offer.id) return
            this.$router.push({ name: 'CreateGlobalOffer', query: { id: String(offer.id) } })
        },
        isSelected(id) {
            return !!this.selectedIds[String(id)]
        },
        toggleSelect(offer, checked) {
            if (!offer || offer.id == null) return
            const key = String(offer.id)
            if (checked) this.$set(this.selectedIds, key, true)
            else this.$delete(this.selectedIds, key)
        },
        toggleSelectPage(checked) {
            ;(this.offers || []).forEach((o) => {
                const key = String(o.id)
                if (checked) this.$set(this.selectedIds, key, true)
                else this.$delete(this.selectedIds, key)
            })
        },
        clearSelection() {
            this.selectedIds = {}
        },
        goMultiEdit() {
            const ids = Object.keys(this.selectedIds)
            if (!ids.length) return
            this.$router.push({
                name: 'CreateGlobalOffer',
                query: { ids: ids.join(',') },
            })
        },
        shortAccount(account) {
            if (!account) return '—'
            return String(account).split(' (')[0].trim() || '—'
        },
        formatDate(value) {
            if (!value) return '—'
            const d = new Date(value)
            if (Number.isNaN(d.getTime())) return String(value)
            const dd = String(d.getDate()).padStart(2, '0')
            const m = String(d.getMonth() + 1)
            const yyyy = d.getFullYear()
            const hh = String(d.getHours()).padStart(2, '0')
            const mm = String(d.getMinutes()).padStart(2, '0')
            return dd + '/' + m + '/' + yyyy + ' ' + hh + ':' + mm
        },
        syncComboQueriesFromFilters() {
            COMBO_KEYS.forEach((key) => {
                this.$set(this.comboQuery, key, this.filters[key] || '')
            })
        },
        filteredComboOptions(key) {
            const options = this.filterOptions[key] || []
            const q = String(this.comboQuery[key] || '').trim().toLowerCase()
            if (!q || q === String(this.filters[key] || '').toLowerCase()) return options
            return options.filter((opt) => String(opt).toLowerCase().indexOf(q) !== -1)
        },
        closeAllCombos() {
            COMBO_KEYS.forEach((key) => {
                this.$set(this.comboOpen, key, false)
                this.$set(this.comboQuery, key, this.filters[key] || '')
            })
        },
        openCombo(key) {
            COMBO_KEYS.forEach((k) => {
                this.$set(this.comboOpen, k, k === key)
                if (k !== key) this.$set(this.comboQuery, k, this.filters[k] || '')
            })
        },
        toggleCombo(key) {
            if (this.comboOpen[key]) {
                this.$set(this.comboOpen, key, false)
                this.$set(this.comboQuery, key, this.filters[key] || '')
                return
            }
            this.openCombo(key)
        },
        onComboInput(key, value) {
            this.$set(this.comboQuery, key, value)
            this.$set(this.comboOpen, key, true)
        },
        selectCombo(key, value) {
            this.filters[key] = value || ''
            this.$set(this.comboQuery, key, value || '')
            this.$set(this.comboOpen, key, false)
            this.page = DEFAULT_PAGE
            this.fetchOffers()
            this.syncUrlFromState()
        },
        clearCombo(key) {
            this.selectCombo(key, '')
        },
        applyQueryFromRoute() {
            this._ignoreSearchWatch = true
            if (this._searchTimer) {
                clearTimeout(this._searchTimer)
                this._searchTimer = null
            }
            const query = this.$route.query || {}
            this.filters = filtersFromQuery(query)
            this.sort = sortFromQuery(query)
            this.dir = dirFromQuery(query)
            this.page = pageFromQuery(query)
            this.limit = limitFromQuery(query)
            this.searchQ = this.filters.q || ''
            this.syncComboQueriesFromFilters()
            this.$nextTick(() => {
                this._ignoreSearchWatch = false
            })
        },
        buildQuery() {
            const query = {}
            const q = String(this.searchQ || '').trim()
            if (q) query.q = q
            FILTER_KEYS.forEach((key) => {
                if (key === 'q') return
                const value = String(this.filters[key] || '').trim()
                if (value) query[key] = value
            })
            if (this.sort && this.sort !== DEFAULT_SORT) query.sort = this.sort
            if (this.dir && this.dir !== DEFAULT_DIR) query.dir = this.dir
            if (this.page && this.page !== DEFAULT_PAGE) query.page = String(this.page)
            if (this.limit && this.limit !== DEFAULT_LIMIT) query.limit = String(this.limit)
            return query
        },
        syncUrlFromState() {
            const query = this.buildQuery()
            if (queryEqual(query, this.$route.query || {})) return Promise.resolve()
            this._syncingFromRoute = true
            return this.$router.replace({ name: 'NewGlobalOffers', query }).catch(() => {}).finally(() => {
                this.$nextTick(() => {
                    this._syncingFromRoute = false
                })
            })
        },
        scheduleSearchFetch() {
            if (this._searchTimer) clearTimeout(this._searchTimer)
            this._searchTimer = setTimeout(() => {
                this._searchTimer = null
                this.filters.q = String(this.searchQ || '').trim()
                this.page = DEFAULT_PAGE
                this.fetchOffers()
                this.syncUrlFromState()
            }, SEARCH_DEBOUNCE_MS)
        },
        setSort(key) {
            if (this.sort === key) {
                this.dir = this.dir === 'asc' ? 'desc' : 'asc'
            } else {
                this.sort = key
                this.dir = key === 'title' || key === 'account' || key === 'bundle_name' ? 'asc' : 'desc'
            }
            this.page = DEFAULT_PAGE
            this.fetchOffers()
            this.syncUrlFromState()
        },
        goToPage(nextPage) {
            const p = Number(nextPage)
            if (!Number.isFinite(p) || p < 1 || p > this.totalPages || p === this.page) return
            this.page = p
            this.fetchOffers()
            this.syncUrlFromState()
        },
        setLimit(nextLimit) {
            const n = Number(nextLimit)
            if (!Number.isFinite(n) || LIMIT_OPTIONS.indexOf(n) === -1 || n === this.limit) return
            this.limit = n
            this.page = DEFAULT_PAGE
            this.fetchOffers()
            this.syncUrlFromState()
        },
        clearFilters() {
            if (this._searchTimer) {
                clearTimeout(this._searchTimer)
                this._searchTimer = null
            }
            this._ignoreSearchWatch = true
            this.searchQ = ''
            this.filters = emptyFilters()
            this.sort = DEFAULT_SORT
            this.dir = DEFAULT_DIR
            this.page = DEFAULT_PAGE
            this.limit = DEFAULT_LIMIT
            this.syncComboQueriesFromFilters()
            this.closeAllCombos()
            this.$nextTick(() => {
                this._ignoreSearchWatch = false
                this.fetchOffers()
                this.syncUrlFromState()
            })
        },
        async fetchOffers() {
            this.loading = true
            this.error = ''
            try {
                const params = {
                    ...this.buildQuery(),
                    sort: this.sort || DEFAULT_SORT,
                    dir: this.dir || DEFAULT_DIR,
                    page: this.page || DEFAULT_PAGE,
                    limit: this.limit || DEFAULT_LIMIT,
                    _t: Date.now(),
                }
                const r = await this.$http.get(this.resources.GlobalOffers.listNewGlobalOffers, { params })
                let data = r.body
                if (typeof data === 'string') {
                    data = JSON.parse(data)
                } else if (!data || data.res == null) {
                    data = JSON.parse(r.bodyText || '{}')
                }
                if (!data || data.res !== 'OK') {
                    this.error = (data && typeof data.data === 'string' && data.data) || 'Failed to load offers'
                    if (!this.offers.length) {
                        this.offers = []
                        this.total = 0
                    }
                    return
                }

                const payload = data.data || {}
                this.offers = payload.offers || []
                this.total = payload.total != null ? Number(payload.total) : this.offers.length
                if (payload.page != null) {
                    const serverPage = Number(payload.page)
                    if (Number.isFinite(serverPage) && serverPage > 0) this.page = serverPage
                }
                if (payload.limit != null) {
                    const serverLimit = Number(payload.limit)
                    if (Number.isFinite(serverLimit) && serverLimit > 0) this.limit = serverLimit
                }
                // Keep URL in sync if server clamped page
                this.syncUrlFromState()
                const opts = payload.filter_options || {}
                this.filterOptions = {
                    attribution: opts.attribution || [],
                    offer_type: opts.offer_type || [],
                    mmp: opts.mmp || [],
                    country: opts.country || [],
                }
            } catch (err) {
                console.error('listNewGlobalOffers failed', err)
                this.error =
                    (err && err.body && err.body.data) ||
                    (err && err.bodyText) ||
                    (err && err.message) ||
                    'Failed to load offers'
                if (!this.offers.length) {
                    this.offers = []
                    this.total = 0
                }
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style lang="css" scoped>
.ngo-page {
    min-height: 100%;
    padding: 24px;
    color: #0f172a;
    font-family: Inter, system-ui, -apple-system, sans-serif;
    box-sizing: border-box;
    text-align: left;
}

.ngo-header {
    margin-bottom: 18px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.ngo-header h1 {
    font-size: 2rem;
    line-height: 1.1;
    font-weight: 800;
    margin: 0;
}

.ngo-sub {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 0.95rem;
}

.ngo-primary-btn {
    appearance: none;
    border: 0;
    background: #2563eb;
    color: #fff;
    font-weight: 700;
    font-size: 0.95rem;
    padding: 10px 16px;
    border-radius: 10px;
    cursor: pointer;
    white-space: nowrap;
}

.ngo-primary-btn:hover {
    background: #1d4ed8;
}

.ngo-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
    align-items: flex-start;
}

.ngo-input,
.ngo-clear-btn {
    height: 36px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #fff;
    color: #0f172a;
    font-size: 0.875rem;
}

.ngo-input {
    padding: 0 10px;
}

.ngo-search {
    min-width: 220px;
    flex: 1 1 200px;
}

.ngo-combo {
    position: relative;
    width: 160px;
    z-index: 1;
}

.ngo-combo.is-open {
    z-index: 30;
}

.ngo-combo-wrap {
    position: relative;
}

.ngo-combo-input {
    width: 100%;
    padding-right: 52px;
}

.ngo-combo-clear,
.ngo-combo-chevron {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: 0;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    padding: 2px 4px;
    line-height: 1;
}

.ngo-combo-clear {
    right: 26px;
}

.ngo-combo-chevron {
    right: 4px;
}

.ngo-combo-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
    max-height: 220px;
    overflow-y: auto;
}

.ngo-combo-dropdown li button {
    display: block;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    padding: 8px 12px;
    font-size: 0.875rem;
    color: #0f172a;
    cursor: pointer;
}

.ngo-combo-dropdown li button:hover,
.ngo-combo-dropdown li button.active {
    background: #eff6ff;
    color: #1d4ed8;
}

.ngo-combo-empty {
    padding: 8px 12px;
    color: #94a3b8;
    font-size: 0.8rem;
}

.ngo-clear-btn {
    padding: 0 12px;
    cursor: pointer;
    background: #f8fafc;
}

.ngo-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 16px;
}

.ngo-status,
.ngo-error,
.ngo-count {
    margin: 0 0 12px;
    font-size: 0.95rem;
}

.ngo-error {
    color: #b91c1c;
}

.ngo-count {
    color: #64748b;
    margin: 0;
}

.ngo-table-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.ngo-meta-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.ngo-multi-edit-btn {
    padding: 8px 14px;
    font-size: 0.875rem;
}

.ngo-th-check,
.ngo-td-check {
    width: 36px;
    text-align: center;
    vertical-align: middle;
}

.ngo-table tbody tr.is-selected {
    background: #eff6ff;
}

.ngo-limit {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ngo-limit-select {
    width: auto;
    min-width: 72px;
    padding-right: 24px;
}

.ngo-pager {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 14px;
    align-items: center;
}

.ngo-page-btn {
    appearance: none;
    min-width: 36px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #fff;
    color: #0f172a;
    font-size: 0.875rem;
    cursor: pointer;
}

.ngo-page-btn:hover:not(:disabled):not(.is-ellipsis) {
    border-color: #93c5fd;
    color: #1d4ed8;
}

.ngo-page-btn.active {
    background: #2563eb;
    border-color: #2563eb;
    color: #fff;
    font-weight: 700;
}

.ngo-page-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.ngo-page-btn.is-ellipsis {
    border-color: transparent;
    background: transparent;
    cursor: default;
    min-width: 24px;
    padding: 0 4px;
}

.ngo-table-wrap {
    overflow-x: auto;
}

.ngo-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
}

.ngo-table th,
.ngo-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    vertical-align: top;
    white-space: nowrap;
}

.ngo-table th {
    font-weight: 700;
    color: #475569;
    background: #f8fafc;
}

.ngo-th-sort {
    cursor: pointer;
    user-select: none;
}

.ngo-th-sort.is-active {
    color: #1d4ed8;
}

.ngo-table td.ngo-title {
    max-width: 350px;
    width: 350px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.ngo-title-link {
    appearance: none;
    display: inline-flex;
    align-items: flex-start;
    gap: 8px;
    max-width: 100%;
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    color: #0f172a;
    font: inherit;
    font-weight: 600;
    line-height: 1.35;
    text-align: left;
    cursor: pointer;
    transition: color 0.15s ease;
}

.ngo-title-text {
    min-width: 0;
}

.ngo-title-icon {
    flex-shrink: 0;
    margin-top: 3px;
    font-size: 11px;
    color: #94a3b8;
    opacity: 0;
    transform: translateX(-2px);
    transition: opacity 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.ngo-title-link:hover,
.ngo-title-link:focus {
    color: #2563eb;
    outline: none;
}

.ngo-title-link:hover .ngo-title-icon,
.ngo-title-link:focus .ngo-title-icon {
    opacity: 1;
    color: #2563eb;
    transform: translateX(0);
}

.ngo-table tbody tr:hover .ngo-title-icon {
    opacity: 0.7;
}

.ngo-mono {
    font-family: 'IBM Plex Mono', ui-monospace, monospace;
    font-size: 0.8rem;
}

.ngo-bundle-name {
    font-weight: 600;
    margin-bottom: 2px;
    white-space: normal;
}

.ngo-advertiser-name {
    font-weight: 600;
    margin-bottom: 2px;
    white-space: normal;
}

.ngo-muted {
    color: #64748b;
}
</style>
